/**
 * Desktop table for the Rules page.
 *
 * Thin wrapper: renders the <thead> based on visible columns
 * from the Customize drawer, maps each rule to a RuleRow, and
 * renders a sticky-ish footer with pagination info. All data
 * decisions (filtering, sorting, mutations) live in the parent
 * page component; this file only cares about layout.
 *
 * Column order and visibility are driven by `visibleColumns` from
 * useRulesCustomization. The Priority column header is sortable
 * (click toggles direction). The checkbox column is always
 * rendered as the first column; it is not managed by the
 * Customize drawer.
 *
 * Mobile users do not see this table; they see RuleCard instead.
 * The parent branches on useBreakpoint and renders the right
 * component tree.
 */

import type { CSSProperties } from "react";
import type {
  Rule,
  RuleColumn,
  RuleColumnId,
  RuleSortField,
  RuleSortDirection,
} from "../rulesTypes";
import { RuleRow } from "./RuleRow";

const PALETTE = {
  ink: "#0E1A1A",
  ink3: "#3A4A52",
  line: "#D8DEE6",
  lineSoft: "#E7EAF0",
};

interface RulesTableProps {
  rules: Rule[];
  visibleColumns: RuleColumn[];
  sortBy: RuleSortField;
  sortDirection: RuleSortDirection;
  rowDensity: "comfortable" | "compact";
  selectedIds: Set<string>;
  onSelectRule: (ruleId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  onSortChange: (field: RuleSortField) => void;
  onToggleStatus: (ruleId: string, next: boolean) => void;
  onToggleAutoPost: (ruleId: string, next: boolean) => void;
  onOpenMenu: (rule: Rule, anchorEl: HTMLElement) => void;
}

export function RulesTable({
  rules,
  visibleColumns,
  sortBy,
  sortDirection,
  rowDensity,
  selectedIds,
  onSelectRule,
  onSelectAll,
  onSortChange,
  onToggleStatus,
  onToggleAutoPost,
  onOpenMenu,
}: RulesTableProps) {
  const allSelected = rules.length > 0 && selectedIds.size === rules.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  return (
    <div style={wrapStyle}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={headCheckboxStyle}>
              <CheckboxCell
                checked={allSelected}
                indeterminate={someSelected}
                onChange={(checked) => onSelectAll(checked)}
                ariaLabel="Select all rules"
              />
            </th>
            {visibleColumns.map((column) => (
              <HeaderCell
                key={column.id}
                column={column}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {rules.map((rule) => (
            <RuleRow
              key={rule.id}
              rule={rule}
              visibleColumns={visibleColumns}
              rowDensity={rowDensity}
              isSelected={selectedIds.has(rule.id)}
              onSelectChange={(selected) => onSelectRule(rule.id, selected)}
              onToggleStatus={(next) => onToggleStatus(rule.id, next)}
              onToggleAutoPost={(next) => onToggleAutoPost(rule.id, next)}
              onOpenMenu={(anchorEl) => onOpenMenu(rule, anchorEl)}
            />
          ))}
        </tbody>
      </table>

      <div style={footerStyle}>
        <div>
          {rules.length} of {rules.length} {rules.length === 1 ? "rule" : "rules"}
        </div>
        <div>Page 1 of 1</div>
      </div>
    </div>
  );
}

interface HeaderCellProps {
  column: RuleColumn;
  sortBy: RuleSortField;
  sortDirection: RuleSortDirection;
  onSortChange: (field: RuleSortField) => void;
}

function HeaderCell({ column, sortBy, sortDirection, onSortChange }: HeaderCellProps) {
  const sortableField = columnToSortField(column.id);
  const isSortable = sortableField !== null;
  const isActive = isSortable && sortBy === sortableField;

  const style: CSSProperties = {
    ...headCellStyle,
    width: COLUMN_WIDTHS[column.id],
    cursor: isSortable ? "pointer" : "default",
  };

  const handleClick = () => {
    if (isSortable && sortableField) onSortChange(sortableField);
  };

  return (
    <th
      style={style}
      onClick={handleClick}
      aria-sort={isActive ? (sortDirection === "asc" ? "ascending" : "descending") : undefined}
    >
      {column.label}
      {isActive && (
        <span style={sortArrowStyle} aria-hidden="true">
          {sortDirection === "asc" ? "▲" : "▼"}
        </span>
      )}
    </th>
  );
}

function columnToSortField(columnId: RuleColumnId): RuleSortField | null {
  switch (columnId) {
    case "priority":
      return "priority";
    case "name":
      return "name";
    default:
      return null;
  }
}

/** Per-column fixed widths (keeps the table steady across tabs). */
const COLUMN_WIDTHS: Record<RuleColumnId, number | undefined> = {
  priority: 90,
  name: undefined,
  appliedTo: 150,
  conditions: undefined,
  settings: 190,
  autoPost: 80,
  status: 72,
  actions: 44,
};

interface CheckboxCellProps {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (checked: boolean) => void;
  ariaLabel: string;
}

/**
 * Minimal checkbox styled to match the Novala ink primary. Used
 * both in the header (select all) and inside RuleRow (per-row).
 * Lives here as a shared helper to avoid duplication.
 */
export function CheckboxCell({
  checked,
  indeterminate,
  onChange,
  ariaLabel,
}: CheckboxCellProps) {
  const style: CSSProperties = {
    width: 15,
    height: 15,
    border: `1.5px solid ${checked || indeterminate ? PALETTE.ink : PALETTE.line}`,
    borderRadius: 3,
    background: checked || indeterminate ? PALETTE.ink : "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  };

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? "mixed" : checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      style={style}
    >
      {indeterminate && (
        <span
          style={{
            width: 8,
            height: 2,
            background: "#ffffff",
            borderRadius: 1,
          }}
        />
      )}
      {checked && !indeterminate && (
        <span
          style={{
            color: "#ffffff",
            fontSize: 10,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          ✓
        </span>
      )}
    </button>
  );
}

const wrapStyle: CSSProperties = {
  width: "100%",
  background: "#ffffff",
};

const tableStyle: CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
};

const headCellStyle: CSSProperties = {
  fontSize: 10.5,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: PALETTE.ink3,
  fontWeight: 700,
  textAlign: "left",
  padding: "12px 14px",
  borderBottom: `1px solid ${PALETTE.line}`,
  background: "#ffffff",
  whiteSpace: "nowrap",
  userSelect: "none",
};

const headCheckboxStyle: CSSProperties = {
  ...headCellStyle,
  width: 36,
};

const sortArrowStyle: CSSProperties = {
  display: "inline-block",
  marginLeft: 4,
  color: PALETTE.ink,
  fontSize: 10,
};

const footerStyle: CSSProperties = {
  padding: "12px 32px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderTop: `1px solid ${PALETTE.lineSoft}`,
  fontSize: 12,
  color: PALETTE.ink3,
  background: "#ffffff",
};